import { PageHeader, PageShell } from '@/components/PageShell';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { type DataTableColumn, DataTable } from '@/components/DataTable';
import { UserAvatar } from '@/components/UserAvatar';
import { useDebouncedSearchParam, useSetSearchParam } from '@/lib/searchParams';
import { useServerSort } from '@/lib/tableSort';
import { currentUserIdState } from '@/state/auth.state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { usersState, userRolesState } from '@/state/user.state';
import logger from '@/util/logger';
import { UserRoleDao, useLoadMore } from '@rock/db-admin';
import { type User, type UserId, UserIdSchema } from '@rock/types';
import { isTestUser } from '@rock/util';
import { useAtomValue } from 'jotai';
import { MoreHorizontal } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const log = logger('UsersListPage');
const userRoleDao = new UserRoleDao();

const PROVIDER_LABELS: Record<string, string> = {
    'google.com': 'Google',
    'apple.com': 'Apple',
    password: 'Password',
    phone: 'Phone',
};

const providerLabel = (providerId: string) => PROVIDER_LABELS[providerId] ?? providerId;

const formatLastLogin = (iso: string) => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export const UsersListPage: FC = () => {
    const {
        data: users,
        hasMore,
        hasError,
        isLoading,
        loadMore,
        filter,
        setFilter,
        sort,
        setSort,
    } = useLoadMore(usersState);
    const roleEntries = useAsyncAtomValue(userRolesState, []);
    const currentUserUid = useAtomValue(currentUserIdState);
    const currentUserId = currentUserUid ? UserIdSchema.parse(currentUserUid) : undefined;

    // Filters live in the URL so they survive refresh and the row-click → details → back round trip.
    const [searchParams] = useSearchParams();
    const setSearchParam = useSetSearchParam();
    const { input: emailInput, onChange: handleEmailChange } = useDebouncedSearchParam('email');
    const emailPrefix = (searchParams.get('email') ?? '').toLowerCase();
    const showTestUsers = searchParams.get('test') === '1';
    const toggleTestUsers = () => setSearchParam('test', showTestUsers ? undefined : '1');
    useEffect(() => {
        if (filter.emailPrefix !== emailPrefix || filter.includeTest !== showTestUsers) {
            setFilter({ emailPrefix, includeTest: showTestUsers });
        }
    }, [emailPrefix, showTestUsers, filter, setFilter]);

    const adminIds = useMemo(
        () => new Set(roleEntries.filter((entry) => entry.roles.admin === true).map((entry) => entry.userId)),
        [roleEntries],
    );
    // Read-admins can view the list but not change roles; only true admins see the row actions.
    const canManageRoles = !!currentUserId && adminIds.has(currentUserId);

    // Per-user in-flight flags so a row's actions are disabled while its write is pending.
    const [pending, setPending] = useState<Record<string, boolean>>({});
    // When an admin revokes their *own* admin, confirm first (the most likely lockout mistake).
    const [selfRevokeUser, setSelfRevokeUser] = useState<User | null>(null);

    const applyAdmin = useCallback(async (user: User, enabled: boolean) => {
        setPending((prev) => ({ ...prev, [user.id]: true }));
        try {
            await userRoleDao.setAdmin(user.id, enabled);
            toast.success(enabled ? 'Admin granted' : 'Admin revoked', {
                description: `${user.email ?? user.id} is ${enabled ? 'now an admin' : 'no longer an admin'}.`,
            });
            log.info('Updated admin role', { userId: user.id, enabled });
        } catch (err) {
            log.error('Failed to update admin role', { err });
            toast.error('Unable to update admin role', {
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
            });
        } finally {
            setPending((prev) => ({ ...prev, [user.id]: false }));
        }
    }, []);

    const handleRevoke = useCallback(
        (user: User) => {
            if (user.id === currentUserId) {
                setSelfRevokeUser(user);
                return;
            }
            void applyAdmin(user, false);
        },
        [currentUserId, applyAdmin],
    );

    const confirmSelfRevoke = () => {
        const user = selfRevokeUser;
        setSelfRevokeUser(null);
        if (user) {
            void applyAdmin(user, false);
        }
    };

    const columns = useMemo(
        () =>
            userColumns({
                adminIds,
                currentUserId,
                canManageRoles,
                pending,
                onPromote: (user) => void applyAdmin(user, true),
                onRevoke: handleRevoke,
            }),
        [adminIds, currentUserId, canManageRoles, pending, applyAdmin, handleRevoke],
    );

    // An email search pins Firestore's ordering to the email it searches.
    const sortDisabledReason = (column: DataTableColumn<User>) =>
        filter.emailPrefix && column.sortField !== 'email'
            ? 'Sorting by this column is unavailable while searching — the search pins the order to User.'
            : undefined;
    const { sort: tableSort, setSort: setTableSort } = useServerSort(columns, { sort, setSort }, sortDisabledReason);

    return (
        <PageShell>
            <PageHeader
                title="Users"
                description="Everyone who has signed into the app. Use a row’s menu to promote or revoke admin access. New admins must have signed in at least once to appear here."
            />
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <CardTitle>Users</CardTitle>
                            <CardDescription>
                                {canManageRoles
                                    ? 'Hover a row to promote or revoke admin access. You can’t lock yourself out without confirming.'
                                    : 'Read-only — you need admin access to change roles.'}
                            </CardDescription>
                        </div>
                        <div className="flex items-center gap-3">
                            <Input
                                placeholder="Search by email…"
                                value={emailInput}
                                onChange={(e) => handleEmailChange(e.target.value)}
                                className="w-[240px]"
                            />
                            <Button variant="ghost" size="sm" onClick={toggleTestUsers}>
                                {showTestUsers ? 'Hide test users' : 'Show test users'}
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {hasError ? (
                        <div className="py-6 text-center section-description">
                            Couldn’t load users. You may not have permission to view this page.
                        </div>
                    ) : users.length === 0 ? (
                        <div className="py-6 text-center section-description">
                            {isLoading
                                ? 'Loading users…'
                                : showTestUsers
                                  ? 'No users found.'
                                  : 'No users found — use “Show test users” to include seeded accounts.'}
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            rows={users}
                            rowKey={(user) => user.id}
                            rowHref={(user) => `/directory/users/${encodeURIComponent(user.id)}`}
                            rowClassName="group"
                            sort={tableSort}
                            onSortChange={setTableSort}
                            sortDisabledReason={sortDisabledReason}
                        />
                    )}
                    {!hasError && hasMore && (
                        <div className="mt-4 flex justify-center">
                            <Button variant="outline" onClick={() => loadMore()}>
                                Load More
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
            <AlertDialog open={!!selfRevokeUser} onOpenChange={(open) => !open && setSelfRevokeUser(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove your own admin access?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You’re about to revoke admin from yourself. You’ll lose access to admin-only features and
                            won’t be able to grant it back from here — it would need to be restored via the Firebase
                            console.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmSelfRevoke}>Remove admin</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </PageShell>
    );
};

/**
 * Providers is a badge list with no single value to order by, and the actions column has no header to
 * click. User sorts by the stored `email` rather than the displayed name — the name is optional, and
 * an admin looking someone up is looking up their email.
 */
function userColumns(options: {
    adminIds: Set<UserId>;
    currentUserId: UserId | undefined;
    canManageRoles: boolean;
    pending: Record<string, boolean>;
    onPromote: (user: User) => void;
    onRevoke: (user: User) => void;
}): DataTableColumn<User>[] {
    const { adminIds, currentUserId, canManageRoles, pending, onPromote, onRevoke } = options;
    return [
        {
            key: 'user',
            header: 'User',
            className: 'min-w-[260px]',
            sortField: 'email',
            sortNote:
                'Sorted by email address, which isn’t always the name shown — accounts with no email (phone and hidden-email sign-ins) are hidden.',
            cell: (user) => (
                <div className="flex flex-row items-center gap-3">
                    <UserAvatar imageUrl={user.imageUrl} name={user.name ?? user.email ?? user.id} className="size-8" />
                    <div className="flex min-w-0 flex-col">
                        <span className="flex flex-wrap items-center gap-2 font-medium">
                            {user.name ?? user.email ?? user.id}
                            {adminIds.has(user.id) && <Badge variant="default">ADMIN</Badge>}
                            {user.id === currentUserId && <Badge variant="secondary">YOU</Badge>}
                            {isTestUser(user) && <Badge variant="outline">Test</Badge>}
                        </span>
                        {user.name && user.email && <span className="text-xs text-muted-foreground">{user.email}</span>}
                    </div>
                </div>
            ),
        },
        {
            key: 'providers',
            header: 'Providers',
            className: 'min-w-[160px]',
            cell: (user) => (
                <div className="flex flex-wrap gap-1">
                    {user.providerIds.length === 0 ? (
                        <span className="text-muted-foreground">—</span>
                    ) : (
                        user.providerIds.map((providerId) => (
                            <Badge key={providerId} variant="outline">
                                {providerLabel(providerId)}
                            </Badge>
                        ))
                    )}
                </div>
            ),
        },
        {
            key: 'lastLogin',
            header: 'Last login',
            className: 'min-w-[180px]',
            cellClassName: 'text-muted-foreground',
            sortField: 'lastLoginAt',
            sortFirst: 'desc',
            cell: (user) => formatLastLogin(user.lastLoginAt),
        },
        {
            key: 'actions',
            className: 'w-[60px]',
            cellClassName: 'text-right',
            cell: (user) =>
                canManageRoles ? (
                    // Stops the click here, before it reaches the row and navigates.
                    <div onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    disabled={!!pending[user.id]}
                                    aria-label="User actions"
                                    className="opacity-0 max-md:opacity-100 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                                >
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {adminIds.has(user.id) ? (
                                    <DropdownMenuItem variant="destructive" onSelect={() => onRevoke(user)}>
                                        Revoke Admin
                                    </DropdownMenuItem>
                                ) : (
                                    <DropdownMenuItem onSelect={() => onPromote(user)}>
                                        Promote to Admin
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                ) : null,
        },
    ];
}
